import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isBusiness } from '@/lib/roles';

// Routes accessible even when a business is in pending_contract status
const CONTRACT_ALLOWED_PATHS = [
  '/business/contract',
  '/restaurant/profile',
  '/restaurant-auth',
];

export default function ProtectedRoute({ children }) {
  const { user, loading, authReady, checkAuth } = useAuth();
  const location = useLocation();

  // If auth is not ready yet (e.g. after callback navigation), try to check
  useEffect(() => {
    if (!authReady && !loading && !user) {
      checkAuth();
    }
  }, [authReady, loading, user, checkAuth]);

  // Still loading or auth not resolved — show spinner
  if (loading || (!authReady && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Auth resolved but no user — redirect to landing
  if (authReady && !user) {
    return <Navigate to="/" replace />;
  }

  // Gate: business owners with pending contract MUST sign before accessing operational areas.
  // Admin bypasses. Regular users bypass. Business w/ 'active' or legacy (no field) also bypass.
  if (isBusiness(user) && user?.account_status === 'pending_contract') {
    const path = location.pathname;
    const allowed = CONTRACT_ALLOWED_PATHS.some(p => path === p || path.startsWith(p + '/'));
    if (!allowed) {
      return <Navigate to="/business/contract" replace />;
    }
  }

  return children;
}
